// @ts-strict-ignore
/**
 * @fileoverview > handler for the "/engagement/upload_asset" endpoint
 */
import { FetchRequestError } from "@lib/errors";
import fetch from "@lib/fetch";
import { getToken } from "@lib/fetch/utils/getToken";
import { postMessageWithMessagePort } from "@lib/postMessageWithMessagePort";

import type { PostEngagementUploadAssetProps } from "../mediaLibrary";
import { reactQueryMutationAdapter } from "../reactQueryFetcherAdapters";
import { engagementUploadAssetCacheKeys, iframeUploadAssetMessage } from "./constants";
import type { EngagementUploadAssetResult, UploadAssetMessage } from "./types";

export const postEngagementUploadAsset = reactQueryMutationAdapter(
  engagementUploadAssetCacheKeys,
  ({
    file,
    allowAnimatedGifs = true,
    territoryIds = [],
    onProgress,
    appGroupId,
    useMessageChannelPort,
    serializedFile,
  }: PostEngagementUploadAssetProps): Promise<EngagementUploadAssetResult> => {
    const body = new FormData();
    // File cannot be passed through MessageChannel API. As a workaround,
    // we pass in the serialized file and deserialize it here.
    // The file is serialized using ArrayBuffer ensure efficiency and avoid size overhead.
    if (useMessageChannelPort && serializedFile) {
      const { fileName, fileType, fileBuffer } = serializedFile;
      // Convert ArrayBuffer back to Blob
      const blob = new Blob([fileBuffer], { type: fileType });
      // Convert Blob to File
      const file = new File([blob], fileName, { type: fileType });
      body.append("image_url", file);
    } else {
      body.append("image_url", file);
    }

    body.append("allow_animated_gifs", allowAnimatedGifs ? "true" : "false");
    territoryIds.forEach((territory) => {
      body.append("territory_ids[]", territory);
    });

    const createUploadPromise = (
      progressHandler: (event: ProgressEvent<XMLHttpRequestEventTarget>) => void
    ): Promise<EngagementUploadAssetResult> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `/engagement/upload_asset?app_group_id=${appGroupId}`, true);

        // Set headers
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        xhr.setRequestHeader("X-CSRF-Token", getToken());
        xhr.setRequestHeader("AB-App-Group-Id", appGroupId);

        // Add an event listener for the 'progress' event
        xhr.upload.addEventListener("progress", progressHandler);
        xhr.upload.addEventListener("error", () => {
          reject(new FetchRequestError(xhr.status, "Error uploading file"));
        });

        xhr.onreadystatechange = () => {
          if (xhr.readyState === XMLHttpRequest.DONE) {
            const status = xhr.status;
            if (status === 0 || (status >= 200 && status < 400)) {
              // The request has been completed successfully
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                reject(new FetchRequestError(xhr.status, "Invalid JSON response"));
              }
            }
          }
        };

        xhr.send(body);
      });
    };

    if (onProgress) {
      return createUploadPromise((event) => onProgress(event.loaded));
    }

    // Functions, e.g. onProgress, are not supported to be passed through MessageChannel or PostMessage APIs.
    // As a workaound, we pass in a boolean flag to indicate the communication should be done through MessageChannel.
    if (useMessageChannelPort) {
      let port;
      // Get the message channel port for communication
      const messageHandler = (event: MessageEvent<UploadAssetMessage>) => {
        if (event.data.type === iframeUploadAssetMessage.assetUploadInitPort) {
          port = event.ports[0];

          // Remove the event listener once the port is set
          window.removeEventListener("message", messageHandler);
        }
      };

      window.addEventListener("message", messageHandler);

      return createUploadPromise((event) => {
        const payload: UploadAssetMessage = {
          type: iframeUploadAssetMessage.assetUploadProgressUpdate,
          progress: event.loaded,
        };
        postMessageWithMessagePort<UploadAssetMessage>(port, payload);
      });
    }

    return fetch(
      "/engagement/upload_asset",
      "POST",
      // this allows us to support upload progress in useUpload
      {},
      { body },
      { setContentType: false }
    );
  }
);
